import { FieldDef, GroupDef, GuiDef, ValidatorDef, TabDefinition, CollapsableDef } from '@/objects/gui';
import { Station } from '@/objects/generated';
import { markRaw, computed } from 'vue';
import { InputText, ValidatedText, InputInt, ValidatedInt, InputSelect, DataSourceLookup, InputBool, DisplayBool } from '@/components';
import { useCommonStore, useEventCommonStore } from '@/stores';
import { storeToRefs } from 'pinia';
import type { SelectLine, SelectLineValueType } from '@/objects/SelectLine';

export class StationGuiDef extends GuiDef<Station> {
    constructor() {
        super('Station');

        const Common = storeToRefs(useCommonStore());
        const StationTypeSource = computed(() => {
            return Common.StationTypeAsSelectLine.value;
        });
        const EventCommon = storeToRefs(useEventCommonStore());
        const StationsWithNoneSource = computed(() => {
            return EventCommon.StationsWithNoneAsSelectLine.value;
        });

        const Name = new FieldDef<Station>();
        Name.Name = 'Name';
        Name.FieldName = 'Name';
        Name.DefinedFor = 'Both';
        Name.TranslationKey = 'station.name';
        Name.Editable = true;
        Name.Filterable = true;
        Name.Sortable = true;
        Name.CssClasses = '';
        Name.value = (station) => station.Name;
        Name.setValue = (station, value) => (station.Name = value);
        Name.DetailEditor = markRaw(InputText);
        Name.DetailEditorProps = {};
        Name.CellEditor = markRaw(InputText);
        Name.CellEditorProps = {};
        Name.CellRenderer = markRaw(ValidatedText);
        Name.CellRendererProps = {};
        Name.Validator = null;
        Name.SortDirection = undefined;
        Name.cellClassRules = undefined;
        Name.MinWidth = undefined;
        Name.MaxWidth = undefined;
        Name.Tooltip = null;
        this.Fields.push(Name);

        const SortOrder = new FieldDef<Station>();
        SortOrder.Name = 'SortOrder';
        SortOrder.FieldName = 'SortOrder';
        SortOrder.DefinedFor = 'Both';
        SortOrder.TranslationKey = 'station.sortorder';
        SortOrder.Editable = true;
        SortOrder.Filterable = false;
        SortOrder.Sortable = true;
        SortOrder.CssClasses = '';
        SortOrder.value = (station) => station.SortOrder;
        SortOrder.setValue = (station, value) => {
            if (value === null || value === undefined || value === '' || isNaN(value)) {
                value = 0;
            }
            station.SortOrder = value;
        };
        SortOrder.DetailEditor = markRaw(InputInt);
        SortOrder.DetailEditorProps = {};
        SortOrder.CellEditor = markRaw(InputInt);
        SortOrder.CellEditorProps = {};
        SortOrder.CellRenderer = markRaw(ValidatedInt);
        SortOrder.CellRendererProps = {};
        SortOrder.Validator = null;
        SortOrder.SortDirection = undefined;
        SortOrder.cellClassRules = undefined;
        SortOrder.MinWidth = undefined;
        SortOrder.MaxWidth = undefined;
        SortOrder.Tooltip = null;
        this.Fields.push(SortOrder);

        const StationAlias = new FieldDef<Station>();
        StationAlias.Name = 'StationAlias';
        StationAlias.FieldName = 'StationAlias';
        StationAlias.DefinedFor = 'Both';
        StationAlias.TranslationKey = 'station.stationalias';
        StationAlias.Editable = false;
        StationAlias.Filterable = true;
        StationAlias.Sortable = true;
        StationAlias.CssClasses = '';
        StationAlias.value = (station) => station.StationAlias;
        StationAlias.setValue = () => {};
        StationAlias.DetailEditor = markRaw(InputText);
        StationAlias.DetailEditorProps = {};
        StationAlias.CellEditor = markRaw(InputText);
        StationAlias.CellEditorProps = {};
        StationAlias.CellRenderer = markRaw(ValidatedText);
        StationAlias.CellRendererProps = {};
        StationAlias.Validator = null;
        StationAlias.SortDirection = undefined;
        StationAlias.cellClassRules = undefined;
        StationAlias.MinWidth = undefined;
        StationAlias.MaxWidth = undefined;
        StationAlias.Tooltip = null;
        this.Fields.push(StationAlias);

        const StationTypeID = new FieldDef<Station>();
        StationTypeID.Name = 'StationTypeID';
        StationTypeID.FieldName = 'StationTypeID';
        StationTypeID.DefinedFor = 'Both';
        StationTypeID.TranslationKey = 'station.stationtypeid';
        StationTypeID.Editable = true;
        StationTypeID.Filterable = true;
        StationTypeID.Sortable = true;
        StationTypeID.CssClasses = '';
        StationTypeID.value = (station) => station.StationTypeID;
        StationTypeID.setValue = (station, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            station.StationTypeID = value;
        };
        StationTypeID.DetailEditor = markRaw(InputSelect);
        StationTypeID.DetailEditorProps = { datasource: { mapsource: StationTypeSource } };
        StationTypeID.CellEditor = markRaw(InputSelect);
        StationTypeID.CellEditorProps = { datasource: { mapsource: StationTypeSource } };
        // @ts-expect-error @ts-ignore
        StationTypeID.CellRenderer = markRaw(DataSourceLookup);
        StationTypeID.CellRendererProps = { datasource: { mapsource: StationTypeSource } };
        StationTypeID.ExportValueFormatter = (obj: Station) => {
            return StationTypeSource.value.get(obj.StationTypeID)?.text;
        };
        StationTypeID.Validator = null;
        StationTypeID.SortDirection = undefined;
        StationTypeID.cellClassRules = undefined;
        StationTypeID.MinWidth = undefined;
        StationTypeID.MaxWidth = undefined;
        StationTypeID.Tooltip = null;
        this.Fields.push(StationTypeID);

        const BackupFor = new FieldDef<Station>();
        BackupFor.Name = 'BackupFor';
        BackupFor.FieldName = 'BackupFor';
        BackupFor.DefinedFor = 'Both';
        BackupFor.TranslationKey = 'station.backupfor';
        BackupFor.Editable = true;
        BackupFor.Filterable = true;
        BackupFor.Sortable = true;
        BackupFor.CssClasses = '';
        BackupFor.value = (station) => station.BackupFor;
        BackupFor.setValue = (station, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            station.BackupFor = value;
        };
        BackupFor.DetailEditor = markRaw(InputSelect);
        BackupFor.DetailEditorProps = { datasource: { mapsource: StationsWithNoneSource } };
        BackupFor.CellEditor = markRaw(InputSelect);
        BackupFor.CellEditorProps = { datasource: { mapsource: StationsWithNoneSource } };
        // @ts-expect-error @ts-ignore
        BackupFor.CellRenderer = markRaw(DataSourceLookup);
        BackupFor.CellRendererProps = { datasource: { mapsource: StationsWithNoneSource } };
        BackupFor.ExportValueFormatter = (obj: Station) => {
            return StationsWithNoneSource.value.get(obj.BackupFor)?.text;
        };
        BackupFor.Validator = null;
        BackupFor.SortDirection = undefined;
        BackupFor.cellClassRules = undefined;
        BackupFor.MinWidth = undefined;
        BackupFor.MaxWidth = undefined;
        BackupFor.Tooltip = null;
        this.Fields.push(BackupFor);

        const UseUsedTime = new FieldDef<Station>();
        UseUsedTime.Name = 'UseUsedTime';
        UseUsedTime.FieldName = 'UseUsedTime';
        UseUsedTime.DefinedFor = 'Both';
        UseUsedTime.TranslationKey = 'station.useusedtime';
        UseUsedTime.Editable = true;
        UseUsedTime.Filterable = false;
        UseUsedTime.Sortable = true;
        UseUsedTime.CssClasses = '';
        UseUsedTime.value = (station) => station.UseUsedTime;
        UseUsedTime.setValue = (station, value) => (station.UseUsedTime = value);
        UseUsedTime.DetailEditor = markRaw(InputBool);
        UseUsedTime.DetailEditorProps = { label: 'station.useusedtime' };
        UseUsedTime.CellEditor = markRaw(InputBool);
        UseUsedTime.CellEditorProps = {};
        UseUsedTime.CellRenderer = markRaw(DisplayBool);
        UseUsedTime.CellRendererProps = {};
        UseUsedTime.Validator = null;
        UseUsedTime.SortDirection = undefined;
        UseUsedTime.cellClassRules = undefined;
        UseUsedTime.MinWidth = undefined;
        UseUsedTime.MaxWidth = undefined;
        UseUsedTime.Tooltip = null;
        this.Fields.push(UseUsedTime);

        const UsedTimeSetsStarttime = new FieldDef<Station>();
        UsedTimeSetsStarttime.Name = 'UsedTimeSetsStarttime';
        UsedTimeSetsStarttime.FieldName = 'UsedTimeSetsStarttime';
        UsedTimeSetsStarttime.DefinedFor = 'Both';
        UsedTimeSetsStarttime.TranslationKey = 'station.usedtimesetsstarttime';
        UsedTimeSetsStarttime.Editable = true;
        UsedTimeSetsStarttime.Filterable = false;
        UsedTimeSetsStarttime.Sortable = true;
        UsedTimeSetsStarttime.CssClasses = '';
        UsedTimeSetsStarttime.value = (station) => station.UsedTimeSetsStarttime;
        UsedTimeSetsStarttime.setValue = (station, value) => (station.UsedTimeSetsStarttime = value);
        UsedTimeSetsStarttime.DetailEditor = markRaw(InputBool);
        UsedTimeSetsStarttime.DetailEditorProps = { label: 'station.usedtimesetsstarttime' };
        UsedTimeSetsStarttime.CellEditor = markRaw(InputBool);
        UsedTimeSetsStarttime.CellEditorProps = {};
        UsedTimeSetsStarttime.CellRenderer = markRaw(DisplayBool);
        UsedTimeSetsStarttime.CellRendererProps = {};
        UsedTimeSetsStarttime.Validator = null;
        UsedTimeSetsStarttime.SortDirection = undefined;
        UsedTimeSetsStarttime.cellClassRules = undefined;
        UsedTimeSetsStarttime.MinWidth = undefined;
        UsedTimeSetsStarttime.MaxWidth = undefined;
        UsedTimeSetsStarttime.Tooltip = null;
        this.Fields.push(UsedTimeSetsStarttime);
    }

    public async LoadDependencies() {
        await useCommonStore().EnsureLoaded(['StationType']);
        await useEventCommonStore().EnsureLoaded(['Stations']);
    }
}
